/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-unused-expressions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/aria-role */
// eslint-disable-next-line no-use-before-define
import React, { useEffect, useMemo, useState } from 'react'

import { Button, Input, Select } from '@centivo/react-components'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import classNames from 'classnames'

import { CATEGORY } from 'src/refactor/helpers/constants'
import { TAddressSuggestion, TServiceLine } from 'src/shared/types'
import { useWatcher } from 'src/hooks'
import { useGlobalState } from 'src/context/global'

import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from 'styled-components'
import { client } from 'src/api'
import { useDebounce } from 'src/refactor/hooks/use-debounce'
import { getSearchSchema } from './schema'
import { ISearchBarForm } from './helpers/types'
import { SearchForm } from './SearchBarForm.styled'

interface IFormParams {
  serviceLines?: string
  name?: string
  placeId: string
  addressLabel: string
}

interface IAddressOptions {
  value: string
  label: string
}

export const SearchBarForm: React.FC<ISearchBarForm> = ({
  category,
  onSubmit,
  defaultValues,
}) => {
  const [isLoading] = useState(false)
  const [openSpecialtySelect, setOpenSpecialtySelect] = useState(false)
  const [openAddressSelect, setOpenAddressSelect] = useState(false)
  const [addressSelectHasFocus, setFocusOnAddressSelect] = useState(false)
  const [inputSearch, setInputSearch] = useState<string>()
  const [state] = useGlobalState()
  const [addressOptions, setAddressOptions] = useState<IAddressOptions[]>([])
  const [addressInputValue, setAddressInputValue] = useState('')
  const [autoSuggestLoading, setAutoSuggestLoading] = useState(false)

  const schema = getSearchSchema(category)

  const {
    register,
    handleSubmit: formHandleSubmit,
    control,
    resetField,
    formState: { errors },
    clearErrors,
  } = useForm<IFormParams>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues,
    shouldFocusError: false,
    resolver: yupResolver(schema),
  })

  const theme = useTheme()

  useEffect(() => {
    setAddressOptions([
      {
        label: defaultValues?.addressLabel ?? '',
        value: defaultValues?.placeId ?? '',
      },
    ])
  }, [])

  useWatcher(
    () => {
      resetField('serviceLines')
      resetField('name')
      clearErrors()
    },
    { category },
    { shouldRunOnMount: false }
  )

  const handleSubmit: SubmitHandler<IFormParams> = formParams => {
    onSubmit({
      ...formParams,
      specialtyCode: formParams.serviceLines ?? undefined,
      category,
      addressLabel:
        (addressOptions ?? []).find(({ value }) => formParams.placeId === value)
          ?.label ?? '',
    })
  }

  const mapServiceLines = (items: TServiceLine[]) =>
    items.map(({ label }) => ({
      label,
      value: label,
    }))

  const currentCategoryOptions = useMemo(() => {
    if (!state.serviceLines) {
      return []
    }

    return category === CATEGORY.DOCTOR
      ? mapServiceLines(state.serviceLines?.doctor)
      : mapServiceLines(state.serviceLines?.facility)
  }, [category, state.serviceLines])
  const selectPlaceholder =
    category === CATEGORY.DOCTOR
      ? 'Select a specialty'
      : 'Select a facility type (required)'
  const namePlaceholder =
    category === CATEGORY.DOCTOR
      ? 'Search by doctor or practice name'
      : 'Search by facility name'

  const addressHelpText = errors.placeId?.message ? undefined : (
    <>
      Required<span className="validation-asterisk">*</span>
    </>
  )

  const replaceCountryInOptions = (value: string) => {
    return value.replace(', USA', '')
  }

  const handleSpecialtySelectOnFocus = (trigger: boolean) => {
    setTimeout(() => {
      !trigger ? setOpenSpecialtySelect(true) : null
    }, 200)
    return null
  }

  const handleAdressSelectOnFocus = (value: string) => {
    // OBS: HANDLER TO ADA REQUIREMENTS
    if (value.length > 1) {
      setAddressInputValue(value)
      setTimeout(() => {
        setOpenAddressSelect(value.length > 1)
      }, 400)
    }
    return null
  }

  const mapAddresses = (addressInfo: TAddressSuggestion[]) => {
    const addressOptionsSuggested = addressInfo.map(
      ({ place_id: placeId, suggestion }) => ({
        value: placeId,
        label: replaceCountryInOptions(suggestion),
      })
    )

    setAddressOptions(addressOptionsSuggested)
  }

  const getAutoSuggestAddresses = async (addressInput: string) => {
    const addressesAutoSuggested = await client.getAutoSuggestPlaces(
      addressInput
    )
    mapAddresses(addressesAutoSuggested)
    setAutoSuggestLoading(false)
  }

  const debouncedAddressInputValue = useDebounce(addressInputValue, 500)

  useEffect(() => {
    if (debouncedAddressInputValue) getAutoSuggestAddresses(addressInputValue)
  }, [debouncedAddressInputValue])

  useEffect(() => {
    if (addressInputValue) {
      setAutoSuggestLoading(true)
      clearErrors('placeId')
    } else setAutoSuggestLoading(false)
  }, [addressInputValue])

  return (
    <SearchForm onSubmit={formHandleSubmit(handleSubmit)}>
      <div
        onClick={() => setOpenSpecialtySelect(!openSpecialtySelect)}
        className={openSpecialtySelect ? 'select-input-focus' : ''}
      >
        <Controller
          name="serviceLines"
          control={control}
          rules={{ required: category === CATEGORY.FACILITY }}
          render={({ field }) => {
            const { value } = field

            const selectValue = value || null
            const shouldShowPcpBorder =
              !inputSearch ||
              'primary care provider'.includes(inputSearch.toLowerCase())

            const { onChange, ...rest } = field

            return (
              <Select
                {...rest}
                aria-label={selectPlaceholder}
                onChange={
                  onChange as (
                    val: string | number | (string | number)[] | null
                  ) => void
                }
                value={selectValue}
                className={classNames(`category-${category}`, {
                  'border--second-child': shouldShowPcpBorder,
                  error: errors.serviceLines?.message,
                })}
                placeholder={selectPlaceholder}
                options={currentCategoryOptions}
                errorText={errors.serviceLines?.message}
                onInputChange={setInputSearch}
                onSelect={() => setOpenSpecialtySelect(false)}
                isClearable
                onFocus={() =>
                  handleSpecialtySelectOnFocus(openSpecialtySelect)
                }
                dropdownIcons={
                  theme.template !== 'template-4'
                    ? undefined
                    : {
                        iconClosed: 'slice-chevron-down',
                        iconOpen: 'slice-chevron-up',
                      }
                }
                clearIcon={
                  theme.template !== 'template-4' ? undefined : 'slice-cancel'
                }
                float
                menuIsOpen={openSpecialtySelect}
              />
            )
          }}
        />
      </div>
      <Input
        {...register('name')}
        aria-label={namePlaceholder}
        id="name-search"
        className="name-search search-bar__form-input"
        label={namePlaceholder}
        placeholder={namePlaceholder}
        errorText={errors.name?.message}
        helpText="The search field is sensitive to spelling and punctuation (like hyphens and apostrophes)"
        icon={theme.template !== 'template-4' ? 'search' : 'slice-search'}
        iconPlacement="left"
        isClearable
        clearIcon={theme.template !== 'template-4' ? undefined : 'slice-cancel'}
        float
      />
      <div
        aria-label="Enter address, city or ZIP*"
        className={
          openAddressSelect || addressSelectHasFocus ? 'select-input-focus' : ''
        }
      >
        <Controller
          name="placeId"
          control={control}
          render={({ field }) => {
            const { value, onChange } = field
            const selectedAddress = value || null
            return (
              <Select
                aria-label="Enter address, city or ZIP*"
                blurInputOnSelect
                id="placeId"
                value={selectedAddress}
                onChange={onChange}
                options={addressOptions}
                onInputChange={handleAdressSelectOnFocus}
                onSelect={() => setOpenAddressSelect(false)}
                onFocus={() => setFocusOnAddressSelect(true)}
                onBlur={() => setFocusOnAddressSelect(false)}
                isClearable
                isSearchable
                className={`zip-search search-bar__form-input centivo--select__value-container ${
                  errors.placeId?.message ? 'error' : ''
                }`}
                placeholder="Enter address, city or ZIP*"
                helpText={addressHelpText}
                errorText={errors.placeId?.message}
                isLoading={autoSuggestLoading}
                hideDropdownIcon
                shouldHideSeparator
                clearIcon={
                  theme.template !== 'template-4' ? undefined : 'slice-cancel'
                }
                float
                noResultsFoundMessage="No results found"
                menuIsOpen={openAddressSelect}
              />
            )
          }}
        />
      </div>
      <Button
        className="submit-search"
        isLoading={isLoading}
        isDisabled={isLoading}
        type="submit"
      >
        Search
      </Button>
    </SearchForm>
  )
}
